<template>
  <main class="gestion-membresias">
    <!-- Header -->
    <div class="view-header mb-4">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h3 class="mb-1 font-weight-bold text-dark">
            <i class="fa fa-credit-card mr-2 text-primary"></i>
            Gestión de Membresías
          </h3>
          <p class="text-muted mb-0">
            Consulta y administra las membresías de un socio (Principales y Adicionales).
          </p>
        </div>
        <div v-if="socio.rut" class="text-right small text-muted">
          <i class="fa fa-clock-o mr-1"></i>
          Última consulta: {{ ultimaConsulta }}
        </div>
      </div>
    </div>

    <!-- Editor de membresía individual -->
    <membresia-editor
      v-if="editingMembresia"
      :membresia="selectedMembresia"
      :tipo="selectedTipo"
      :socio="socio"
      :catalogos="catalogos"
      @close="cerrarEditor"
      @saved="onMembresiaActualizada"
    />

    <!-- Buscador + listado (oculto cuando se está editando) -->
    <template v-if="!editingMembresia">
    <!-- Buscador por RUT -->
    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <form @submit.prevent="buscarMembresias">
          <div class="row align-items-end">
            <div class="col-md-6 col-lg-5">
              <label class="form-label small text-muted mb-1">
                <strong>RUT del Socio</strong>
              </label>
              <input
                type="text"
                class="form-control"
                placeholder="Ej: 76903216-9"
                v-model="rutInput"
                :disabled="loading"
                @keyup.enter="buscarMembresias"
                ref="rutInput"
              />
              <small class="form-text text-muted">
                Ingresa el RUT con guión y dígito verificador.
              </small>
            </div>
            <div class="col-md-3 col-lg-2 mt-3 mt-md-0">
              <button
                type="submit"
                class="btn btn-primary btn-block"
                :disabled="!rutInput || loading"
              >
                <i class="fa fa-search mr-2"></i>
                Buscar
              </button>
            </div>
            <div class="col-md-3 col-lg-2 mt-3 mt-md-0" v-if="socio.rut">
              <button
                type="button"
                class="btn btn-outline-secondary btn-block"
                @click="limpiar"
                :disabled="loading"
              >
                <i class="fa fa-eraser mr-2"></i>
                Limpiar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Consultando membresías del socio...</p>
    </div>

    <!-- Resultados -->
    <template v-else-if="socio.rut && consultaRealizada">
      <!-- Tarjeta de socio -->
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-7">
              <h5 class="mb-1 font-weight-bold">
                <i class="fa fa-user-circle mr-2 text-primary"></i>
                {{ socio.nombreSocio || 'Sin nombre' }}
              </h5>
              <p class="text-muted mb-2">
                <i class="fa fa-id-card mr-1"></i>
                <strong>RUT:</strong> {{ socio.rut }}
              </p>
              <div v-if="socio.accesoSucursales && socio.accesoSucursales.length">
                <small class="text-muted d-block mb-1"><strong>Sucursales con acceso:</strong></small>
                <span
                  v-for="acceso in socio.accesoSucursales"
                  :key="acceso._id"
                  class="badge mr-1 mb-1"
                  :class="getAccesoBadgeClass(acceso.estadoAcceso)"
                >
                  {{ acceso.nombreSucursal }}
                  <span class="ml-1" style="opacity: 0.85;">
                    · {{ acceso.estadoAcceso && acceso.estadoAcceso.glosa || 'N/A' }}
                  </span>
                </span>
              </div>
            </div>
            <div class="col-md-5 text-md-right mt-3 mt-md-0">
              <div class="d-inline-block mr-3">
                <div class="text-muted small">Activas (Principal)</div>
                <div class="font-weight-bold h4 text-success mb-0">
                  {{ planesActivos.length }}
                </div>
              </div>
              <div class="d-inline-block">
                <div class="text-muted small">Adicionales (Secundario)</div>
                <div class="font-weight-bold h4 text-info mb-0">
                  {{ planesAdicionales.length }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Membresías Activas (Principal) -->
      <section class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <i class="fa fa-star text-success mr-2"></i>
            Membresías Activas
            <span class="badge badge-success ml-2">Principal</span>
          </h5>
          <button
            type="button"
            class="btn btn-sm btn-outline-success"
            @click="onAsignarMembresia('principal')"
          >
            <i class="fa fa-plus mr-1"></i>
            Asignar Membresía Principal
          </button>
        </div>

        <div v-if="!planesActivos.length" class="card border-0 shadow-sm">
          <div class="card-body text-center py-5 text-muted">
            <i class="fa fa-info-circle fa-2x mb-3"></i>
            <p class="mb-2">Este socio no tiene membresías Activas (Principal).</p>
            <button
              type="button"
              class="btn btn-success btn-sm"
              @click="onAsignarMembresia('principal')"
            >
              <i class="fa fa-plus mr-1"></i>
              Asignar primera membresía Principal
            </button>
          </div>
        </div>

        <div v-else class="row">
          <div
            v-for="membresia in planesActivos"
            :key="membresia._id"
            class="col-md-6 col-xl-4 mb-3"
          >
            <membresia-card
              :membresia="membresia"
              tipo="principal"
              @ver-detalle="onVerDetalleMembresia"
              @eliminar="onEliminarMembresia"
            />
          </div>
        </div>
      </section>

      <!-- Membresías Adicionales (Secundario) -->
      <section class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <i class="fa fa-plus-circle text-info mr-2"></i>
            Membresías Adicionales
            <span class="badge badge-info ml-2">Secundario</span>
          </h5>
          <button
            type="button"
            class="btn btn-sm btn-outline-info"
            @click="onAsignarMembresia('adicional')"
          >
            <i class="fa fa-plus mr-1"></i>
            Asignar Membresía Adicional
          </button>
        </div>

        <div v-if="!planesAdicionales.length" class="card border-0 shadow-sm">
          <div class="card-body text-center py-5 text-muted">
            <i class="fa fa-info-circle fa-2x mb-3"></i>
            <p class="mb-2">Este socio no tiene membresías Adicionales (Secundario).</p>
            <button
              type="button"
              class="btn btn-info btn-sm"
              @click="onAsignarMembresia('adicional')"
            >
              <i class="fa fa-plus mr-1"></i>
              Asignar primera membresía Adicional
            </button>
          </div>
        </div>

        <div v-else class="row">
          <div
            v-for="membresia in planesAdicionales"
            :key="membresia._id"
            class="col-md-6 col-xl-4 mb-3"
          >
            <membresia-card
              :membresia="membresia"
              tipo="adicional"
              @ver-detalle="onVerDetalleMembresia"
              @eliminar="onEliminarMembresia"
            />
          </div>
        </div>
      </section>
    </template>

    <!-- Estado inicial (sin búsqueda) -->
    <div v-else-if="!socio.rut && !loading" class="card border-0 shadow-sm">
      <div class="card-body text-center py-5 text-muted">
        <i class="fa fa-search fa-3x mb-3 text-primary"></i>
        <h5 class="mb-2">Busca un socio para gestionar sus membresías</h5>
        <p class="mb-0">
          Ingresa el RUT del socio en el campo superior, o ingresa desde el botón
          <strong>"Detalle Membresía"</strong> en Gestión de Socios.
        </p>
      </div>
    </div>
    </template>

    <!-- Modal de asignación de membresía -->
    <asignar-membresia-modal
      :show="asignarModal.show"
      :tipo="asignarModal.tipo"
      :socio="socio"
      :catalogos="catalogos"
      :sucursales="sucursales"
      @close="cerrarAsignarModal"
      @saved="onMembresiaAsignada"
    />
  </main>
</template>

<script>
import MembresiaCard from './components/MembresiaCard.vue';
import MembresiaEditor from './components/MembresiaEditor.vue';
import AsignarMembresiaModal from './components/AsignarMembresiaModal.vue';

export default {
  name: 'GestionMembresias',
  components: { MembresiaCard, MembresiaEditor, AsignarMembresiaModal },
  data() {
    return {
      rutInput: '',
      loading: false,
      consultaRealizada: false,
      socio: {},
      planesActivos: [],
      planesAdicionales: [],
      ultimaConsulta: '',
      selectedMembresia: null,
      selectedTipo: null,
      sucursales: [],
      asignarModal: {
        show: false,
        tipo: 'principal'
      },
      catalogos: {
        unidadesDuracion: [],
        convenios: [],
        unidadesExpiracionRenovacion: [],
        estadosPago: [],
        familias: [],
        subfamilias: []
      }
    };
  },
  computed: {
    editingMembresia() {
      return !!this.selectedMembresia;
    }
  },
  mounted() {
    this.cargarCatalogoMembresias();
    this.cargarCatalogosFamiliasSubFamilias();
    this.cargarSucursalesDesdeSesion();
    const rutQuery = this.$route.query && this.$route.query.rut;
    if (rutQuery) {
      this.rutInput = rutQuery;
      this.buscarMembresias();
    } else {
      this.$nextTick(() => {
        if (this.$refs.rutInput) this.$refs.rutInput.focus();
      });
    }
  },
  watch: {
    '$route.query.rut'(nuevoRut, anteriorRut) {
      if (nuevoRut && nuevoRut !== anteriorRut) {
        this.rutInput = nuevoRut;
        this.buscarMembresias();
      }
    }
  },
  methods: {
    async buscarMembresias() {
      const rut = (this.rutInput || '').trim();
      if (!rut) return;

      this.loading = true;
      this.consultaRealizada = false;

      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: '/v1/membresias/socio/consultar/rut/detallado'
          },
          Auth: {
            Token: sessionStorage.getItem('jCxbraoQeD')
          },
          Parameters: { rut },
          Body: {}
        });

        const result = response.data && response.data.result;
        if (result && result.code === 0) {
          const dataOut = response.data.dataOut || {};
          this.socio = {
            _id: dataOut._id,
            nombreSocio: dataOut.nombreSocio,
            rut: dataOut.rut,
            accesoSucursales: dataOut.accesoSucursales || []
          };
          const planes = dataOut.planes || {};
          this.planesActivos = planes.activos || [];
          this.planesAdicionales = planes.adicionales || [];
          this.ultimaConsulta = new Date().toLocaleString('es-CL');
          this.consultaRealizada = true;

          if (this.$route.query.rut !== rut) {
            this.$router.replace({ query: { ...this.$route.query, rut } }).catch(() => {});
          }
        } else {
          throw new Error((result && result.message) || 'No se pudo consultar al socio');
        }
      } catch (error) {
        const backendMessage =
          error.response &&
          error.response.data &&
          error.response.data.result &&
          error.response.data.result.message;
        this.socio = {};
        this.planesActivos = [];
        this.planesAdicionales = [];
        this.consultaRealizada = false;
        this.$swal.fire({
          title: 'No se pudieron obtener las membresías',
          text: backendMessage || error.message || 'Error inesperado',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading = false;
      }
    },
    limpiar() {
      this.rutInput = '';
      this.socio = {};
      this.planesActivos = [];
      this.planesAdicionales = [];
      this.consultaRealizada = false;
      this.ultimaConsulta = '';
      if (this.$route.query.rut) {
        this.$router.replace({ query: {} }).catch(() => {});
      }
      this.$nextTick(() => {
        if (this.$refs.rutInput) this.$refs.rutInput.focus();
      });
    },
    getAccesoBadgeClass(estadoAcceso) {
      const glosa = (estadoAcceso && estadoAcceso.glosa) || '';
      switch (glosa.toUpperCase()) {
        case 'AUTORIZADO': return 'badge-success';
        case 'BLOQUEADO': return 'badge-danger';
        case 'PENDIENTE': return 'badge-warning';
        default: return 'badge-secondary';
      }
    },
    async cargarCatalogoMembresias() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/membresias/catalogo' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {},
          Body: {}
        });
        const result = response.data && response.data.result;
        if (result && result.code === 0) {
          const dataOut = response.data.dataOut || {};
          this.catalogos = {
            ...this.catalogos,
            unidadesDuracion: dataOut.unidadesDuracion || [],
            convenios: dataOut.convenios || [],
            unidadesExpiracionRenovacion: dataOut.unidadesExpiracionRenovacion || [],
            estadosPago: dataOut.estadosPago || []
          };
        }
      } catch (error) {
        console.error('No se pudo cargar el catálogo de membresías:', error);
      }
    },
    async cargarCatalogosFamiliasSubFamilias() {
      const [familiasRes, subfamiliasRes] = await Promise.allSettled([
        this.consultarCatalogoFamilias(),
        this.consultarCatalogoSubFamilias()
      ]);
      this.catalogos = {
        ...this.catalogos,
        familias: familiasRes.status === 'fulfilled' ? (familiasRes.value || []) : [],
        subfamilias: subfamiliasRes.status === 'fulfilled' ? (subfamiliasRes.value || []) : []
      };
    },
    async consultarCatalogoFamilias() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/planes/familia/catalogo' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {},
          Body: {}
        });
        return (response.data && response.data.dataOut && response.data.dataOut.familias) || [];
      } catch (error) {
        console.error('Error al consultar catálogo de familias:', error);
        return [];
      }
    },
    async consultarCatalogoSubFamilias() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/planes/subfamilia/catalogo' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {},
          Body: {}
        });
        return (response.data && response.data.dataOut && response.data.dataOut.subfamilias) || [];
      } catch (error) {
        console.error('Error al consultar catálogo de subfamilias:', error);
        return [];
      }
    },
    onVerDetalleMembresia({ membresia, tipo }) {
      this.selectedMembresia = membresia;
      this.selectedTipo = tipo;
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },
    cerrarEditor() {
      this.selectedMembresia = null;
      this.selectedTipo = null;
    },
    async onMembresiaActualizada() {
      this.cerrarEditor();
      await this.buscarMembresias();
    },
    async onEliminarMembresia({ membresia, tipo }) {
      if (!this.socio.rut) {
        this.$swal.fire({
          title: 'No hay socio cargado',
          text: 'Busca primero al socio antes de eliminar una membresía.',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      if (!membresia || !membresia.descripcionPlan) {
        this.$swal.fire({
          title: 'Membresía sin descripción',
          text: 'No se puede eliminar una membresía que no tiene descripción de plan.',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }

      const tipoPlan = tipo === 'principal' ? 'PRINCIPAL' : 'SECUNDARIO';
      const tipoLabel = tipo === 'principal' ? 'Principal (Activa)' : 'Secundaria (Adicional)';

      const confirm = await this.$swal.fire({
        title: '¿Eliminar membresía?',
        html:
          `<div class="text-left">` +
          `<p class="mb-2">Vas a eliminar la siguiente membresía del socio <strong>${this.socio.nombreSocio || ''}</strong> (RUT <strong>${this.socio.rut}</strong>):</p>` +
          `<ul class="mb-2">` +
          `<li><strong>Tipo:</strong> ${tipoLabel}</li>` +
          `<li><strong>Plan:</strong> ${membresia.descripcionPlan}</li>` +
          `<li><strong>Familia / Sub:</strong> ${membresia.nombreFamilia || '—'} · ${membresia.nombreSubFamilia || '—'}</li>` +
          `</ul>` +
          `<p class="text-danger mb-0"><i class="fa fa-exclamation-triangle mr-1"></i> Esta acción no se puede revertir.</p>` +
          `</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#eb6532',
        cancelButtonColor: '#6bd098',
        focusCancel: true
      });
      if (!confirm.isConfirmed) return;

      this.$swal.fire({
        title: 'Eliminando membresía...',
        text: 'Por favor espera un momento.',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => { this.$swal.showLoading(); }
      });

      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/membresias/socio/eliminar' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {
            rut: this.socio.rut,
            tipoPlan,
            modalidadPlan: 'ON-LINE',
            descripcionPlan: membresia.descripcionPlan
          },
          Body: {}
        });

        const result = response.data && response.data.result;
        if (result && result.code === 0) {
          await this.$swal.fire({
            title: '¡Membresía eliminada!',
            text: result.message || 'La membresía fue eliminada correctamente.',
            icon: 'success',
            confirmButtonColor: '#6bd098'
          });
          await this.buscarMembresias();
        } else {
          throw new Error((result && result.message) || 'No se pudo eliminar la membresía');
        }
      } catch (error) {
        const backendMessage =
          error.response &&
          error.response.data &&
          error.response.data.result &&
          error.response.data.result.message;
        this.$swal.fire({
          title: 'Error al eliminar la membresía',
          text: backendMessage || error.message || 'Error inesperado',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      }
    },
    cargarSucursalesDesdeSesion() {
      try {
        const raw = sessionStorage.getItem('xxxwypt5ip');
        this.sucursales = raw ? JSON.parse(raw) : [];
      } catch (error) {
        console.error('No se pudieron cargar las sucursales desde la sesión:', error);
        this.sucursales = [];
      }
    },
    onAsignarMembresia(tipo) {
      if (!this.socio.rut) {
        this.$swal.fire({
          title: 'Busca un socio primero',
          text: 'Debes consultar a un socio antes de asignarle una membresía.',
          icon: 'info',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      this.asignarModal = { show: true, tipo };
    },
    cerrarAsignarModal() {
      this.asignarModal.show = false;
    },
    async onMembresiaAsignada() {
      this.cerrarAsignarModal();
      await this.buscarMembresias();
    }
  }
};
</script>

<style scoped>
.gestion-membresias {
  padding: 1rem 1.5rem;
}
.badge {
  font-size: 0.78rem;
  padding: 0.35em 0.6em;
}
</style>
